import React from "react";
import { useAppLoadingContext } from "../../Context/AppLoadingContext";
import { AppLoadingContextTypes } from "../../Context/ContextTypes/AppLoadingContextTypes";
import DOMPurify from "dompurify";
import { Box, Card, CardMedia, Grid, Typography } from "@mui/material";
import parse from "html-react-parser";
import "./EarnRewardsBrand.scss";

type EarnRewardsBrandPropsTypes = {
  carouselId: string;
  earnRewardsHeader: string;
  earnRewardsDescription: string;
  redeemRewardsHeader: string;
  redeemRewardsDescription: string;
  earnRewardItems: Array<{
    itemLogo: string;
    itemLogoWidth: string;
    itemLogoHeight: string;
    itemLogoTextAriaLabel: string;
    itemLogoText: string;
    itemLogoDescription: string;
  }>;
  brandTilesItems: Array<{
    itemLogo: string;
    itemLogoWidth: string;
    itemLogoHeight: string;
    itemLogoAltText: string;
  }>;
  isInEditor: boolean;
  cqPath: string;
};
export default function EarnRewardsBrandReact(props: EarnRewardsBrandPropsTypes) {
  const { translationsData } = useAppLoadingContext() as AppLoadingContextTypes;

  const DomPurify = (tag: string) => {
    return DOMPurify.sanitize(translationsData[tag]);
  };

  const translatedData = {
    earnRewardsHeader: DomPurify(props.earnRewardsHeader),
    earnRewardsDescription: DomPurify(props.earnRewardsDescription),
    redeemRewardsHeader: DomPurify(props.redeemRewardsHeader),
    redeemRewardsDescription: DomPurify(props.redeemRewardsDescription),
  };
  return (
    <Box
      component="section"
      id={`${props.carouselId}`}
      className="content-section"
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: { xs: "#F8F8F8", md: "#F8F8F8", sm: "#F8F8F8", lg: "transparent" },
        paddingLeft: "12px",
        paddingRight: "12px",
      }}
    >
      <Grid
        container
        xs={12}
        sm={12}
        md={12}
        lg={12}
        display="flex"
        flexDirection="column"
        maxWidth={{ xs: "360px", sm: "766px", md: "766px", lg: "1296px" }}
        sx={{
          backgroundColor: "#F8F8F8",
          borderRadius: { lg: "40px" },
          paddingTop: "33px",
          paddingBottom: "76px",
        }}
      >
        <Grid item display="flex" flexDirection="column" alignItems="center">
          <Typography variant="h2">{parse(translatedData.earnRewardsHeader)}</Typography>

          <Typography
            variant="body1"
            sx={{
              textAlign: "center",
              margin: { xs: "20px 40px", sm: "20px 150px", md: "20px 150px", lg: "20px 0px" },
              fontSize: "18px",
            }}
          >
            {parse(translatedData.earnRewardsDescription)}
          </Typography>

          <Grid
            container
            xs={12}
            sm={12}
            md={12}
            lg={12}
            display="flex"
            maxWidth={{ xs: "400px", sm: "766px", md: "766px", lg: "1296px" }}
            padding={{ xs: "0% 15%", sm: "0% 25%", md: "0% 25%", lg: "0% 15%" }}
          >
            {props.earnRewardItems?.map((item) => {
              return (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={6}
                  lg={3}
                  display="flex"
                  flexDirection="column"
                  justifyContent="flex-start"
                  alignItems="center"
                  sx={{
                    marginTop: { lg: "", md: "1.5rem", sm: "1.5rem", xs: "1.5rem" },
                  }}
                >
                  <Card
                    className="rewardsIcon"
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      width: "150px",
                      flexDirection: "column",
                      height: "150px",
                      borderRadius: "50%",
                    }}
                  >
                    <CardMedia
                      component="img"
                      sx={{
                        width: `${item.itemLogoWidth}px`,
                        height: `${item.itemLogoHeight}px`,
                        objectFit: "inherit",
                      }}
                      src={DomPurify(item.itemLogo)}
                      alt=""
                      aria-hidden="true"
                    />
                    <Typography
                      variant="body1"
                      fontWeight="700"
                      fontSize={{ xl: "34px", sm: "32px", xs: "28px" }}
                      lineHeight={{ sm: "44px", xs: "36px" }}
                      sx={{ margin: "0px" }}
                      {...(item.itemLogoTextAriaLabel ? {"aria-label": `${DomPurify(item.itemLogoTextAriaLabel)}`} : {})}
                    >
                      {parse(DomPurify(item.itemLogoText))}
                    </Typography>
                  </Card>
                  <Typography
                    variant="body1"
                    margin="10px 0px"
                    sx={{ fontSize: "16px", textAlign: "center" }}
                  >
                    {parse(DomPurify(item.itemLogoDescription))}
                  </Typography>
                </Grid>
              );
            })}
          </Grid>
        </Grid>

        <Grid
          container
          item
          display="flex"
          alignItems="center"
          justifyContent="center"
          marginTop="40px"
        >
          <Grid item xs={12}>
            <Typography variant="h2">{parse(translatedData.redeemRewardsHeader)}</Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography
              variant="body1"
              sx={{
                textAlign: "center",
                margin: { xs: "20px 40px", sm: "20px 150px", md: "20px 150px", lg: "20px 0px" },
                fontSize: "16px",
              }}
            >
              {parse(translatedData.redeemRewardsDescription)}
            </Typography>
          </Grid>

          <Grid container item xs={12} sm={11} lg={8} direction="row" spacing={1.5}>
            {props.brandTilesItems?.map((item) => (
              <Grid container item xs={4} sm={3} justifyContent="center" alignItems="center">
                <Card
                  sx={{
                    width: { lg: "199px", sm: "163px", xs: "103.7px" },
                    height: { lg: "160px", sm: "131px", xs: "88px" },
                    display: "flex",
                    padding: "10px",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <CardMedia
                    component="img"
                    sx={{
                      maxWidth: `${item.itemLogoWidth}px`,
                      maxHeight: `${item.itemLogoHeight}px`,
                    }}
                    src={DomPurify(item.itemLogo)}
                    alt={DomPurify(item.itemLogoAltText)}
                  />
                </Card>
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
}
